"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.canonicalizeUiGraph = exports.canonicalizeDbGraph = exports.artifactIdLabels = exports.canonicalJson = exports.canonicalValue = exports.sortedStrings = void 0;
const graphDiff_1 = require("./graphDiff");
const compareStrings = (left, right) => {
    if (left === right)
        return 0;
    return left < right ? -1 : 1;
};
const sortedStrings = (values) => [...values].sort(compareStrings);
exports.sortedStrings = sortedStrings;
/**
 * Deterministic JSON-able copy of `value`: object keys sorted, `Map`s turned
 * into sorted `[key, value]` pairs, functions dropped, and every string that
 * equals a key of `idLabels` replaced by its label. Ids differ across restores
 * and clones while slugs do not, so labelling ids by slug makes the output
 * comparable across runs.
 */
const canonicalValue = (value, idLabels) => {
    if (typeof value === 'string') {
        return idLabels.get(value) ?? value;
    }
    if (Array.isArray(value)) {
        return value.map((entry) => (0, exports.canonicalValue)(entry, idLabels));
    }
    if (value instanceof Map) {
        return [...value.entries()]
            .map(([key, entry]) => [
            (0, exports.canonicalValue)(key, idLabels),
            (0, exports.canonicalValue)(entry, idLabels),
        ])
            .sort(([left], [right]) => compareStrings(JSON.stringify(left), JSON.stringify(right)));
    }
    if ((0, graphDiff_1.isPlainObject)(value)) {
        return Object.fromEntries(Object.keys(value)
            .sort(compareStrings)
            .filter((key) => typeof value[key] !== 'function')
            .map((key) => [key, (0, exports.canonicalValue)(value[key], idLabels)]));
    }
    return value;
};
exports.canonicalValue = canonicalValue;
const canonicalJson = (value, idLabels) => JSON.stringify((0, exports.canonicalValue)(value, idLabels), null, 1);
exports.canonicalJson = canonicalJson;
const artifactIdLabels = (artifacts) => new Map(artifacts.map(({ id, artifactSlug }) => [id, `artifact:${artifactSlug}`]));
exports.artifactIdLabels = artifactIdLabels;
/**
 * Id-free view of a DB graph: artifacts sorted by slug without `id` /
 * `artifactDocumentId`, and edges as sorted slug pairs.
 */
const canonicalizeDbGraph = ({ artifacts, dependencies, }) => {
    const idLabels = (0, exports.artifactIdLabels)(artifacts);
    const nodes = [...artifacts]
        .sort((left, right) => compareStrings(left.artifactSlug, right.artifactSlug))
        .map(({ id: _id, artifactDocumentId: _documentId, ...artifact }) => (0, exports.canonicalValue)(artifact, idLabels));
    const edges = dependencies
        .map(({ inputArtifactId, outputArtifactId }) => [inputArtifactId, outputArtifactId].map((id) => idLabels.get(id) ?? `missing:${id}`))
        .sort((left, right) => compareStrings(JSON.stringify(left), JSON.stringify(right)));
    return JSON.stringify({ nodes, edges }, null, 1);
};
exports.canonicalizeDbGraph = canonicalizeDbGraph;
/**
 * Id-free view of a UI (operation) graph. Tape nodes whose id is an artifact id
 * are labelled by slug. Other nodes (operation nodes, and the few tape nodes
 * the converter mints with `uuidv4()`) get a structural label: their type plus
 * the labels of their outputs. Nodes that still collide are told apart by
 * their canonical configuration.
 */
const canonicalizeUiGraph = ({ graph, artifacts, }) => {
    const artifactLabels = (0, exports.artifactIdLabels)(artifacts);
    const nodeById = new Map(graph.nodes.map((node) => [node.metadata.tapeBuilderNodeId, node]));
    const outputsById = new Map();
    graph.edges.forEach(({ source, target }) => {
        outputsById.set(source, [...(outputsById.get(source) ?? []), target]);
    });
    const baseLabels = new Map();
    const baseLabel = (id, visiting) => {
        const known = artifactLabels.get(id) ?? baseLabels.get(id);
        if (known !== undefined)
            return known;
        const node = nodeById.get(id);
        if (!node)
            return `missing:${id}`;
        if (visiting.has(id))
            return `cycle:${node.metadata.tapeBuilderNodeType}`;
        visiting.add(id);
        const outputs = (0, exports.sortedStrings)((outputsById.get(id) ?? []).map((output) => baseLabel(output, visiting)));
        visiting.delete(id);
        const label = `${node.metadata.tapeBuilderNodeType}>[${outputs.join(',')}]`;
        baseLabels.set(id, label);
        return label;
    };
    graph.nodes.forEach(({ metadata }) => baseLabel(metadata.tapeBuilderNodeId, new Set()));
    // Disambiguate colliding structural labels by canonical configuration.
    const labels = new Map(artifactLabels);
    const idsByBaseLabel = new Map();
    baseLabels.forEach((label, id) => {
        idsByBaseLabel.set(label, [...(idsByBaseLabel.get(label) ?? []), id]);
    });
    idsByBaseLabel.forEach((ids, label) => {
        if (ids.length === 1) {
            labels.set(ids[0], label);
            return;
        }
        ids
            .map((id) => ({
            id,
            config: (0, exports.canonicalJson)(nodeById.get(id)?.configuration, artifactLabels),
        }))
            .sort((left, right) => compareStrings(left.config, right.config))
            .forEach(({ id }, index) => labels.set(id, `${label}#${index}`));
    });
    const nodes = graph.nodes
        .map((node) => ({
        label: labels.get(node.metadata.tapeBuilderNodeId) ?? 'unlabelled',
        node: (0, exports.canonicalValue)(node, labels),
    }))
        .sort((left, right) => compareStrings(left.label, right.label));
    const edges = graph.edges
        .map(({ source, target }) => [source, target].map((id) => labels.get(id) ?? `missing:${id}`))
        .sort((left, right) => compareStrings(JSON.stringify(left), JSON.stringify(right)));
    return JSON.stringify({ nodes, edges }, null, 1);
};
exports.canonicalizeUiGraph = canonicalizeUiGraph;
//# sourceMappingURL=canonicalize.js.map